import { useEffect, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth-store.js';
import { useRecent } from '../lib/recent-store.js';
import { initials, money0, shortDate } from '../lib/format.js';
import { IconSearch } from '../components/icons.js';
import { QueryError } from '../components/query-error.js';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'hold', label: 'A/R hold' },
] as const;
type Filter = (typeof FILTERS)[number]['key'];

export function ClientsScreen() {
  const user = useAuth((s) => s.user);
  const qc = useQueryClient();
  const recentClients = useRecent((s) => s.clients);

  const search = useSearch({ from: '/clients' });

  const [q, setQ] = useState('');
  const [activeQ, setActiveQ] = useState('');
  const [filter, setFilter] = useState<Filter>(search.filter ?? 'all');
  const [salesperson, setSalesperson] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setActiveQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  // Managers/admins can narrow the full book to one salesperson's accounts;
  // reps are auto-scoped server-side and never see this control.
  const canPickSalesperson = user?.role === 'manager' || user?.role === 'admin';
  const roster = useQuery({
    queryKey: ['salespeople'],
    queryFn: ({ signal }) => api.users.salespeople(signal),
    staleTime: 10 * 60_000,
    enabled: canPickSalesperson,
  });

  const clients = useQuery({
    queryKey: ['customers', activeQ, filter, salesperson],
    queryFn: ({ signal }) =>
      api.customers.search(
        {
          ...(activeQ ? { q: activeQ } : {}),
          // Server-side: client-side filtering of one page misses holds
          // outside the most-recently-active slice (the 39K-customer lesson).
          ...(filter === 'hold' ? { hold: 'true' as const } : {}),
          ...(filter === 'active' ? { hold: 'false' as const } : {}),
          ...(salesperson ? { salesperson } : {}),
        },
        signal,
      ),
  });

  // Warm the detail query so tapping a row opens instantly.
  const prefetch = (spireId: number) =>
    void qc.prefetchQuery({ queryKey: ['customer', spireId], queryFn: () => api.customers.getById(spireId) });

  const filtered = clients.data ?? [];
  const showRecents = !activeQ && filter === 'all' && recentClients.length > 0;

  return (
    <>
      <div className="topbar">
        <div>
          <h2>My Clients</h2>
          <div className="sub">
            {user?.displayName}
            {clients.data ? ` · ${clients.data.length} shown` : ''}
          </div>
        </div>
      </div>
      <div className="search-bar">
        <div className="search-wrap">
          <IconSearch size={16} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients" aria-label="Search clients" />
        </div>
      </div>
      {canPickSalesperson && (
        <div style={{ padding: '0 14px 4px' }}>
          <select
            value={salesperson}
            onChange={(e) => setSalesperson(e.target.value)}
            aria-label="Filter by salesperson"
            style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--kd-line)', borderRadius: 10, fontSize: 14, background: '#fff', color: salesperson ? 'var(--kd-text)' : 'var(--kd-text-3)' }}
          >
            <option value="">All salespeople</option>
            {(roster.data ?? []).map((s) => (
              <option key={s.code} value={s.code}>
                {s.name && s.name !== s.code ? `${s.name} (${s.code})` : s.code}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="filter-chips">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`chip${filter === f.key ? ' active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div style={{ paddingTop: 8 }}>
        {showRecents && (
          <>
            <div className="section-title">Recently visited</div>
            {recentClients.map((rc) => (
              <Link
                key={rc.spireId}
                to="/clients/$id"
                params={{ id: String(rc.spireId) }}
                className="customer-row"
                onPointerEnter={() => prefetch(rc.spireId)}
              >
                <div className="avatar">{initials(rc.name)}</div>
                <div className="body">
                  <div className="name">{rc.name}</div>
                </div>
              </Link>
            ))}
            <div className="section-title">All clients</div>
          </>
        )}

        {clients.isPending && Array.from({ length: 6 }).map((_, i) => <div key={i} className="kd-skeleton" />)}
        {clients.isError && <QueryError message={clients.error.message} onRetry={() => void clients.refetch()} />}
        {clients.isSuccess && filtered.length === 0 && (
          <div className="section-title">No clients{filter !== 'all' ? ' match this filter' : ''}.</div>
        )}

        {filtered.map((c) => (
          <Link
            key={c.spireId}
            to="/clients/$id"
            params={{ id: String(c.spireId) }}
            className="customer-row"
            onPointerEnter={() => prefetch(c.spireId)}
            onFocus={() => prefetch(c.spireId)}
          >
            <div className="avatar">{initials(c.name)}</div>
            <div className="body">
              <div className="name">{c.name}</div>
              <div className="meta">
                {[c.address.city, c.address.provState].filter(Boolean).join(', ') || c.customerNo}
                {c.hold ? ' · A/R hold' : ''}
              </div>
            </div>
            <div className="right">
              <div className="strong">{shortDate(c.lastInvoiceDate)}</div>
              <div>{Number(c.thisYearSales) > 0 ? `${money0(c.thisYearSales)} YTD` : 'no orders'}</div>
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}
